import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Container, CtaBand, Eyebrow, Lead, TextLink } from "@/components/site/primitives";
import { SITE } from "@/lib/site-content";
import { ogImageMeta } from "@/lib/og";
import type { CaseStudy } from "@/content/case-studies";
import { CASE_STUDIES, getCaseStudy } from "@/content/case-studies";
import { contactFunnel } from "@/lib/analytics";

const BASE = "/case-studies";

export const Route = createFileRoute("/case-studies/$slug")({
  loader: ({ params }) => {
    const study = getCaseStudy(params.slug);
    if (!study) throw notFound();
    return { study };
  },
  head: ({ params, loaderData }) => {
    const study = loaderData?.study;
    const url = `${SITE.url}${BASE}/${params.slug}`;
    if (!study) {
      return {
        meta: [{ title: "Case study not found | Scryla" }, { name: "robots", content: "noindex" }],
      };
    }
    return {
      meta: [
        { title: study.metaTitle },
        { name: "description", content: study.metaDescription },
        { property: "og:title", content: study.metaTitle },
        { property: "og:description", content: study.metaDescription },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        ...ogImageMeta("case-study", "Case study, measured outcomes from a technology engagement"),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: study.title,
            description: study.metaDescription,
            url,
            author: { "@type": "Person", name: SITE.person, url: SITE.linkedin },
            publisher: { "@type": "Organization", name: SITE.name, url: SITE.url },
            about: study.sector,
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: SITE.url },
              {
                "@type": "ListItem",
                position: 2,
                name: "Case studies",
                item: `${SITE.url}${BASE}`,
              },
              { "@type": "ListItem", position: 3, name: study.title, item: url },
            ],
          }),
        },
      ],
    };
  },
  notFoundComponent: CaseStudyNotFound,
  component: CaseStudyPage,
});

function CaseStudyNotFound() {
  return (
    <Container>
      <h1 className="text-3xl font-bold tracking-[-0.03em]">Case study not found</h1>
      <p className="mt-4 text-base text-body">That case study does not exist or has moved.</p>
      <p className="mt-6">
        <TextLink to={BASE}>See all case studies</TextLink>
      </p>
    </Container>
  );
}

function CaseStudyPage() {
  const { study } = Route.useLoaderData() as { study: CaseStudy };
  const others = CASE_STUDIES.filter((c) => c.slug !== study.slug);
  const ctaSource = `case_study_${study.slug.replace(/-/g, "_")}`;

  return (
    <div>
      <section
        aria-labelledby="cs-detail-heading"
        className="border-b border-border bg-gradient-to-b from-background to-surface"
      >
        <Container>
          <nav aria-label="Breadcrumb" className="text-sm text-muted-foreground">
            <ol className="flex flex-wrap items-center gap-2">
              <li>
                <Link to="/" className="transition-colors hover:text-primary">
                  Home
                </Link>
              </li>
              <li aria-hidden="true">/</li>
              <li>
                <Link to={BASE} className="transition-colors hover:text-primary">
                  Case studies
                </Link>
              </li>
            </ol>
          </nav>
          <div className="mt-6">
            <Eyebrow>{study.sector}</Eyebrow>
          </div>
          <h1
            id="cs-detail-heading"
            className="mt-4 max-w-[24ch] text-[34px] leading-[1.06] font-bold tracking-[-0.035em] text-balance md:text-5xl"
          >
            {study.title}
          </h1>
          <Lead className="mt-6">{study.summary}</Lead>
          <dl className="mt-8 grid gap-5 border-t border-border pt-6 sm:grid-cols-3">
            <div>
              <dt className="text-xs font-semibold tracking-[0.18em] text-muted-foreground uppercase">
                Client
              </dt>
              <dd className="mt-2 text-[15px] leading-relaxed text-body">{study.client}</dd>
            </div>
            <div>
              <dt className="text-xs font-semibold tracking-[0.18em] text-muted-foreground uppercase">
                My role
              </dt>
              <dd className="mt-2 text-[15px] leading-relaxed text-body">{study.role}</dd>
            </div>
            <div>
              <dt className="text-xs font-semibold tracking-[0.18em] text-muted-foreground uppercase">
                When
              </dt>
              <dd className="mt-2 text-[15px] leading-relaxed text-body">{study.era}</dd>
            </div>
          </dl>
        </Container>
      </section>

      {study.figures.length > 0 ? (
        <section className="border-b border-border bg-surface" aria-labelledby="cs-figures-heading">
          <Container>
            <h2
              id="cs-figures-heading"
              className="text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
            >
              The numbers, as recorded
            </h2>
            <dl className="mt-8 grid gap-5 sm:grid-cols-3">
              {study.figures.map((f) => (
                <div key={f.label} className="rounded-lg border border-border bg-card p-6">
                  <dt className="text-sm font-semibold text-muted-foreground">{f.label}</dt>
                  <dd className="mt-2 text-xl font-bold tracking-[-0.02em] text-ink">{f.value}</dd>
                </div>
              ))}
            </dl>
            <p className="mt-6 max-w-[62ch] text-sm text-muted-foreground">
              Only figures stated in the written engagement record appear here. Where an outcome was
              described without a number, it is described without one below.
            </p>
          </Container>
        </section>
      ) : null}

      <section className="border-b border-border" aria-labelledby="cs-challenge-heading">
        <Container>
          <h2
            id="cs-challenge-heading"
            className="max-w-[24ch] text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            The situation
          </h2>
          <div className="mt-6 max-w-[68ch] space-y-5 text-base leading-relaxed text-body md:text-[19px]">
            {study.challenge.map((p) => (
              <p key={p.slice(0, 40)}>{p}</p>
            ))}
          </div>
        </Container>
      </section>

      {study.sections.map((section, i) => (
        <section
          key={section.heading}
          className={`border-b border-border${i % 2 === 0 ? " bg-surface" : ""}`}
          aria-labelledby={`cs-section-${i}`}
        >
          <Container>
            <h2
              id={`cs-section-${i}`}
              className="max-w-[26ch] text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
            >
              {section.heading}
            </h2>
            {section.paragraphs?.length ? (
              <div className="mt-6 max-w-[68ch] space-y-5 text-base leading-relaxed text-body md:text-[19px]">
                {section.paragraphs.map((p) => (
                  <p key={p.slice(0, 40)}>{p}</p>
                ))}
              </div>
            ) : null}
            {section.points?.length ? (
              <ul className="mt-8 grid gap-5 md:grid-cols-2">
                {section.points.map((point) => (
                  <li
                    key={point.slice(0, 40)}
                    className="rounded-lg border border-border bg-card p-6"
                  >
                    <p className="text-[15px] leading-relaxed text-body">{point}</p>
                  </li>
                ))}
              </ul>
            ) : null}
          </Container>
        </section>
      ))}

      <section className="border-b border-border" aria-labelledby="cs-success-heading">
        <Container>
          <Eyebrow>What made it work</Eyebrow>
          <h2
            id="cs-success-heading"
            className="mt-3 max-w-[26ch] text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            Key to the success
          </h2>
          <ul className="mt-7 grid max-w-[80ch] gap-3 md:grid-cols-2">
            {study.success.map((item) => (
              <li key={item} className="flex gap-3 text-[15px] leading-relaxed text-body">
                <span
                  aria-hidden="true"
                  className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary"
                />
                {item}
              </li>
            ))}
          </ul>
          <p className="mt-8 max-w-[68ch] text-base leading-relaxed text-body md:text-[19px]">
            {study.today}
          </p>
          <p className="mt-7 flex flex-wrap gap-x-8 gap-y-3">
            <TextLink to="/what-i-do">How I work with founders now</TextLink>
            <TextLink to="/board-advisory">Board and advisory roles</TextLink>
          </p>
        </Container>
      </section>

      <section className="border-b border-border bg-surface" aria-labelledby="cs-others-heading">
        <Container>
          <h2
            id="cs-others-heading"
            className="text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            Other case studies
          </h2>
          <ul className="mt-8 grid gap-5 md:grid-cols-2">
            {others.map((c) => (
              <li key={c.slug} className="rounded-lg border border-border bg-card p-6">
                <p className="text-xs font-semibold tracking-[0.18em] text-muted-foreground uppercase">
                  {c.sector}
                </p>
                <h3 className="mt-3 text-lg font-semibold">
                  <Link
                    to="/case-studies/$slug"
                    params={{ slug: c.slug }}
                    className="transition-colors hover:text-primary"
                  >
                    {c.title}
                  </Link>
                </h3>
                <p className="mt-2 text-[15px] leading-relaxed text-body">{c.headline}</p>
              </li>
            ))}
          </ul>
        </Container>
      </section>

      <CtaBand
        eyebrow="Next step"
        heading="Facing something similar?"
        body="Tell me where you are and what is worrying you. If I am not the right person, I will say so."
        to="/contact"
        cta="Start a conversation"
        onCtaClick={() => contactFunnel.ctaClick(ctaSource)}
      />
    </div>
  );
}
